import { Button, Group, SegmentedControl, Select, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconMicrophone, IconPlayerStopFilled, IconRefresh } from '@tabler/icons-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { TextInput } from 'src/Components/Shared/TextInput';
import useFetchMembers from 'src/Hooks/Queries/useFetchMembers';
import useFetchUser from 'src/Hooks/Queries/useFetchUser';
import {
  isVoiceRecordingSupported,
  useVoiceRecorder,
  VoiceRecording,
} from 'src/Hooks/Shared/useVoiceRecorder';
import { formatVoiceDuration } from 'src/Utils/memoryDate';

import styles from './VoiceStoryCapture.module.scss';
import { Props, SpeakerMode } from './VoiceStoryCapture.types';

/** 10 minutes per story (Alexis, 02/11/2026). */
export const MAX_STORY_SECONDS = 600;

/**
 * A story told out loud, presented as form fields: the recording, then "Qui raconte ?".
 * The audio only lives in the browser until the parent sends it, so a network error never
 * loses a story.
 */
const VoiceStoryCapture = ({ label, onChange }: Props) => {
  const { t } = useTranslation();
  const { data: user } = useFetchUser();
  const { data: membersData } = useFetchMembers();

  const [speakerMode, setSpeakerMode] = useState<SpeakerMode>('me');
  const [speakerId, setSpeakerId] = useState<string | null>(null);
  const [speakerName, setSpeakerName] = useState('');
  const [recording, setRecording] = useState<VoiceRecording | null>(null);

  const { isRecording, elapsedSeconds, start, stop } = useVoiceRecorder({
    maxDurationSeconds: MAX_STORY_SECONDS,
    onRecorded: setRecording,
  });

  const memberOptions = useMemo(
    () =>
      (membersData?.members ?? [])
        .filter(member => member.user && member.user.id !== user?.id)
        .map(member => {
          const firstName = member.user.firstName?.trim();
          const fullName = `${member.user.firstName ?? ''} ${member.user.lastName ?? ''}`.trim();
          return {
            value: member.user.id,
            // First name first; a member without one falls back to the full name.
            label: firstName
              ? firstName
              : fullName
                ? fullName
                : t('memories.recorder.unnamedMember', 'Membre sans nom'),
          };
        }),
    [membersData, user?.id, t]
  );

  const speakerIsValid =
    speakerMode === 'me' ||
    (speakerMode === 'member' && !!speakerId) ||
    (speakerMode === 'other' && speakerName.trim().length > 0);

  useEffect(() => {
    onChange(
      recording && speakerIsValid
        ? {
            recording,
            speakerId: speakerMode === 'member' ? (speakerId ?? undefined) : undefined,
            speakerName: speakerMode === 'other' ? speakerName.trim() : undefined,
          }
        : null
    );
    // onChange is a parent setter; re-run only when the story itself changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recording, speakerIsValid, speakerMode, speakerId, speakerName]);

  const showError = (message: string) => notifications.show({ color: 'red', message });

  const handleStart = async () => {
    if (!isVoiceRecordingSupported()) {
      showError(
        t(
          'memories.recorder.unsupported',
          'Ce navigateur ne sait pas enregistrer la voix. Essayez depuis un téléphone ou un autre navigateur.'
        )
      );
      return;
    }
    try {
      await start();
    } catch (startError) {
      const name = (startError as DOMException)?.name;
      showError(
        name === 'NotAllowedError' || name === 'SecurityError'
          ? t(
              'voiceDictation.errors.denied',
              "L'accès au micro est bloqué. Autorise-le dans les réglages du navigateur (icône à gauche de l'adresse du site), puis réessaie."
            )
          : t('voiceDictation.errors.noMicrophone', 'Aucun micro n’a été trouvé sur cet appareil.')
      );
    }
  };

  const speakerError =
    recording && !speakerIsValid
      ? t('memories.recorder.speakerMissing', 'Indiquez qui raconte pour ajouter le récit.')
      : undefined;

  return (
    <>
      <div className={styles.field}>
        {label && <div className={styles.label}>{label}</div>}
        <Group gap="md">
          {!recording && !isRecording && (
            <Button
              variant="outline"
              leftSection={<IconMicrophone size={20} />}
              onClick={handleStart}
            >
              {t('memories.recorder.startLabel', 'Enregistrer le récit')}
            </Button>
          )}
          {isRecording && (
            <>
              <Button color="red" leftSection={<IconPlayerStopFilled size={16} />} onClick={stop}>
                {t('memories.recorder.stopLabel', 'Arrêter')}
              </Button>
              <Text size="sm" c="dimmed">
                {formatVoiceDuration(elapsedSeconds)} / {formatVoiceDuration(MAX_STORY_SECONDS)}
              </Text>
            </>
          )}
          {recording && (
            <>
              <Text size="sm">
                {t('memories.recorder.recorded', 'Récit enregistré : {{duration}}', {
                  duration: formatVoiceDuration(recording.durationSeconds),
                })}
              </Text>
              <Button
                variant="outline"
                leftSection={<IconRefresh size={20} />}
                onClick={() => setRecording(null)}
              >
                {t('memories.recorder.restart', 'Recommencer')}
              </Button>
            </>
          )}
        </Group>
      </div>

      <div className={styles.field}>
        <div className={styles.label}>{t('memories.recorder.whoSpeaks', 'Qui raconte ?')}</div>
        <SegmentedControl
          fullWidth
          value={speakerMode}
          onChange={value => setSpeakerMode(value as SpeakerMode)}
          data={[
            { value: 'me', label: t('memories.recorder.me', 'Moi') },
            { value: 'member', label: t('memories.recorder.member', 'Un membre') },
            { value: 'other', label: t('memories.recorder.other', 'Quelqu’un d’autre') },
          ]}
        />
      </div>
      {speakerMode === 'member' && (
        <Select
          label={t('memories.recorder.memberLabel', 'Membre qui raconte')}
          placeholder={t('memories.recorder.chooseMember', 'Choisir un membre')}
          data={memberOptions}
          value={speakerId}
          onChange={setSpeakerId}
          searchable
          error={speakerError}
        />
      )}
      {speakerMode === 'other' && (
        <TextInput
          label={t('memories.recorder.otherLabel', 'Nom de la personne qui raconte')}
          placeholder={t('memories.recorder.otherExample', 'Ex. Mamie Odette')}
          value={speakerName}
          onChange={event => setSpeakerName(event.currentTarget.value)}
          maxLength={100}
          error={speakerError}
        />
      )}
    </>
  );
};

export default VoiceStoryCapture;
